import React from "react";

export type TableColumn<T> = {
    header: string;
    field?: keyof T;
    render?: (row: any) => React.ReactNode;
    align?: "left" | "center" | "right";
    width?: number | string;
    minWidth?: number | string;
    maxWidth?: number | string;
};

type CustomTableProps<T> = {
    columns: TableColumn<T>[];
    data: T[];
};

const headerCellStyle: React.CSSProperties = {
    border: "1px solid #ddd",
    padding: "10px",
    backgroundColor: "#f9fafb",
    fontFamily: "Arial",
    fontWeight: 400,        
    fontStyle: "normal",
    fontSize: "14px",
    lineHeight: "130%",       
    letterSpacing: "0%",
    color: "#515356",
};

const bodyCellStyle: React.CSSProperties = {
    border: "1px solid #eee",
    padding: "10px",
    verticalAlign: "top",
    fontFamily: "Arial",
    fontWeight: 400,
    fontStyle: "normal",
    fontSize: "14px",
    lineHeight: "20px",      
    letterSpacing: "0%",
    color: "#515356",
};


const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
    tableLayout: "auto",
};

function CustomTable<T extends Record<string, any>>({
    columns,
    data,
}: CustomTableProps<T>): JSX.Element {
    return (
        <table style={tableStyle}>
            <thead>
                <tr>
                    {columns.map((col, index) => (
                        <th
                            key={index}
                            style={{
                                ...headerCellStyle,
                                textAlign: col.align ?? "left",
                                width: col.width,
                                minWidth: col.minWidth,
                                maxWidth: col.maxWidth,
                            }}
                        >
                            {col.header}
                        </th>
                    ))}
                </tr>
            </thead>

            <tbody>
                {data.length === 0 ? (
                    <tr>
                        <td
                            colSpan={columns.length}
                            style={{
                                textAlign: "center",
                                padding: "16px",
                                color: "#888",
                            }}
                        >
                            No data available
                        </td>
                    </tr>
                ) : (
                    data.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                            {columns.map((col, colIndex) => {
                                const cellData = col.field
                                    ? row[col.field]
                                    : undefined;

                                return (
                                    <td
                                        key={colIndex}
                                        style={{
                                            ...bodyCellStyle,
                                            textAlign: col.align ?? "left",
                                            width: col.width,
                                            minWidth: col.minWidth,
                                            maxWidth: col.maxWidth,
                                            wordBreak: "break-word",
                                            overflow: "hidden",
                                        }}
                                    >
                                        {col.render ? (
                                            col.render(row)
                                        ) : Array.isArray(cellData) ? (
                                            cellData.join(", ")
                                        ) : (
                                            cellData ?? "-"
                                        )}
                                    </td>
                                );
                            })}
                        </tr>
                    ))
                )}
            </tbody>
        </table>
    );
}

export default CustomTable;